import { useEffect, useRef, useState } from "react";
import type { ColumnDef } from "../fields";

const GROUPS: ColumnDef["group"][] = ["Model", "Identity", "Price", "Technical"];

/**
 * Column show/hide + "save as my default view". `columns` is the LIVE list
 * built from the filterable_fields registry (fields.ts's buildStockColumns)
 * -- as of the 2026-09-02 stored-feature seeding that's 33+ Technical
 * entries, so a field-finder search box is no longer optional (mock's
 * `.cp-search`, previously skipped when there were only 3).
 *
 * `forcedKeys` are fields referenced by the ACTIVE filter (ui_spec A1: mock's
 * "filter-referenced fields auto-added", `.col-row.auto .tag` "in filter")
 * -- shown checked and un-checkable, since hiding a column you're currently
 * filtering on would hide the very value that explains why a row matched.
 *
 * Persistence is the real per-user `saved_views` endpoint
 * (app/api/routes/saved.py, data_spec 9.6) -- `onSaveDefault` round-trips
 * to the server; this component no longer touches localStorage itself.
 */
export function ColumnsMenu({
  columns,
  visible,
  forcedKeys,
  onChange,
  onSaveDefault,
}: {
  columns: ColumnDef[];
  visible: Set<string>;
  forcedKeys: Set<string>;
  onChange: (next: Set<string>) => void;
  onSaveDefault: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  function toggle(key: string) {
    if (forcedKeys.has(key)) return; // in-filter columns can't be hidden
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  function saveAsDefault() {
    onSaveDefault();
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  const filtered = search.trim()
    ? columns.filter((c) => c.label.toLowerCase().includes(search.trim().toLowerCase()))
    : columns;

  return (
    <div className="results-tools" ref={ref}>
      <button className="btn ghost sm" onClick={() => setOpen((o) => !o)}>
        ⊞ Columns ({visible.size}/{columns.length})
      </button>
      {open && (
        <div className="columns-popover">
          <div className="cp-search">
            <span>⌕</span>
            <input placeholder="Find a field…" value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <div className="cp-scroll">
            {GROUPS.map((group) => {
              const inGroup = filtered.filter((c) => c.group === group);
              if (inGroup.length === 0) return null;
              return (
                <div key={group}>
                  <div className="cp-group-label">{group}</div>
                  {inGroup.map((c) => (
                    <label
                      className={`col-row${forcedKeys.has(c.key) ? " auto" : ""}`}
                      key={c.key}
                      title={forcedKeys.has(c.key) ? "In your active filter -- can't be hidden" : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={visible.has(c.key) || forcedKeys.has(c.key)}
                        disabled={forcedKeys.has(c.key)}
                        onChange={() => toggle(c.key)}
                      />
                      {c.label}
                      {forcedKeys.has(c.key) && <span className="tag">in filter</span>}
                    </label>
                  ))}
                </div>
              );
            })}
            {filtered.length === 0 && <div className="cp-note">No field matches "{search}".</div>}
          </div>
          <div className="cp-note">
            Saved to your account (<b>saved_views</b>, per-user, follows you across devices)
          </div>
          <button className="btn primary sm save-view-btn" onClick={saveAsDefault}>
            {saved ? "✓ Saved" : "Save as my default view"}
          </button>
        </div>
      )}
    </div>
  );
}
